/**
 * Collects (or reuses) an account, the legal consents, then hands over to
 * Stripe Checkout. Existing subscribers switch plan here instead.
 */

import React, { FormEvent, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  accountRequest,
  redirectTo,
  type AccountSummary,
  type PaidPlan,
} from '../../utils/accountApi';
import { BUSINESS } from '../../legal/legalDetails';
import { getPlanDetails, getUserPlan } from '../../utils/plans';

interface CheckoutDialogProps {
  plan: PaidPlan;
  onClose: () => void;
}

type Mode = 'signup' | 'signin';

const inputClass =
  'w-full rounded-lg border-2 border-slate-200 px-3 py-2.5 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none';

const CheckoutDialog: React.FC<CheckoutDialogProps> = ({ plan, onClose }) => {
  const { user, signIn, signUp, setAccount } = useAuth();
  const details = getPlanDetails(plan);
  const currentPlan = getUserPlan(user);
  const isAccount = user?.accountType === 'account';
  const isTeamLogin = !!user && !isAccount;
  const isSubscriber = isAccount && currentPlan !== 'none';

  const [mode, setMode] = useState<Mode>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [immediateStart, setImmediateStart] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switched, setSwitched] = useState(false);

  const price = `£${details.monthlyPrice.toFixed(2)}`;
  const needsAccount = !user;
  const canSubmit =
    !busy &&
    acceptTerms &&
    immediateStart &&
    (!needsAccount || (email.trim() !== '' && password !== ''));

  const startCheckout = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      let token = user?.token;
      if (needsAccount) {
        const account =
          mode === 'signup'
            ? await signUp(email.trim(), password, acceptTerms)
            : await signIn(email.trim(), password);
        if (account.accountType !== 'account') {
          setError('Your team login already includes Pro. No payment is needed.');
          setBusy(false);
          return;
        }
        if (account.account && account.account.plan !== 'none') {
          setError(
            'You already have a subscription. Manage it from your Plan & billing page.'
          );
          setBusy(false);
          return;
        }
        token = account.token;
      }
      const { url } = await accountRequest<{ url: string }>('/billing/checkout', {
        method: 'POST',
        token,
        body: {
          plan,
          accept_terms: acceptTerms,
          immediate_start_ack: immediateStart,
        },
      });
      redirectTo(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setBusy(false);
    }
  };

  const switchPlan = async () => {
    setBusy(true);
    setError(null);
    try {
      const { account } = await accountRequest<{ account: AccountSummary }>(
        '/billing/change-plan',
        { method: 'POST', token: user?.token, body: { plan } }
      );
      setAccount(account);
      setSwitched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const isUpgrade = currentPlan === 'standard' && plan === 'pro';

  let body: React.ReactNode;
  if (isTeamLogin) {
    body = (
      <p className="text-sm text-slate-600">
        Your team login already includes every Pro feature, so there&apos;s
        nothing to buy.
      </p>
    );
  } else if (switched) {
    body = (
      <div className="text-sm text-slate-700">
        <p className="font-semibold text-emerald-700">
          You&apos;re now on {details.name}.
        </p>
        <a
          href="/"
          className="mt-4 inline-block rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white no-underline"
        >
          Start practising
        </a>
      </div>
    );
  } else if (isSubscriber && currentPlan === plan) {
    body = (
      <p className="text-sm text-slate-600">
        You&apos;re already on {details.name}.{' '}
        <a href="/account" className="text-indigo-600 underline">
          Manage your subscription
        </a>
        .
      </p>
    );
  } else if (isSubscriber) {
    body = (
      <div className="text-sm text-slate-700">
        <p>
          {isUpgrade
            ? `Upgrade now and you'll be charged the difference for the rest of this billing month, then ${price} a month.`
            : `Switch now and the unused difference will be credited against your next payment, then ${price} a month.`}{' '}
          Same renewal date, cancel any time.
        </p>
        <button
          type="button"
          onClick={switchPlan}
          disabled={busy}
          className="mt-4 w-full rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-3 font-semibold text-white disabled:opacity-60"
        >
          {busy ? 'Switching…' : `Confirm switch to ${details.name}`}
        </button>
      </div>
    );
  } else {
    body = (
      <form onSubmit={startCheckout} className="space-y-4">
        {needsAccount && (
          <div className="space-y-3">
            <div className="flex rounded-lg bg-slate-100 p-1 text-sm font-medium">
              {(['signup', 'signin'] as Mode[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setMode(option)}
                  className={`flex-1 rounded-md py-1.5 ${
                    mode === option ? 'bg-white text-slate-900 shadow' : 'text-slate-500'
                  }`}
                >
                  {option === 'signup' ? 'Create account' : 'I have an account'}
                </button>
              ))}
            </div>
            <label className="block text-sm font-medium text-slate-700">
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className={`${inputClass} mt-1`}
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                minLength={mode === 'signup' ? 10 : undefined}
                required
                className={`${inputClass} mt-1`}
              />
              {mode === 'signup' && (
                <span className="mt-1 block text-xs font-normal text-slate-500">
                  At least 10 characters.
                </span>
              )}
            </label>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs leading-relaxed text-slate-700">
          <p className="m-0 font-semibold text-slate-900">Before you pay</p>
          <ul className="m-0 mt-2 list-disc space-y-1 pl-4">
            <li>
              <strong>{price} today</strong>, then {price} every month on the
              same date.
            </li>
            <li>
              <strong>Renews automatically until you cancel.</strong> Cancel any
              time from Plan &amp; billing; you keep access to the end of the
              paid month.
            </li>
            <li>
              Cancel within 14 days of subscribing for a refund, minus a
              proportionate amount for the days you&apos;ve used.
            </li>
            <li>Prices in GBP. {BUSINESS.vatNote}.</li>
          </ul>
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-1"
          />
          <span>
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="noreferrer" className="text-indigo-600 underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noreferrer" className="text-indigo-600 underline">
              Privacy Policy
            </a>
            .
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={immediateStart}
            onChange={(e) => setImmediateStart(e.target.checked)}
            className="mt-1"
          />
          <span>
            Start my subscription now. I understand that if I cancel within 14
            days I&apos;ll be refunded minus a proportionate amount for the
            service I&apos;ve used (
            <a href="/refunds" target="_blank" rel="noreferrer" className="text-indigo-600 underline">
              Refunds &amp; Cancellation
            </a>
            ).
          </span>
        </label>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-3 text-base font-semibold text-white shadow-lg transition hover:from-indigo-600 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Redirecting to secure payment…' : 'Continue to secure payment'}
        </button>
        <p className="m-0 text-center text-xs text-slate-500">
          🔒 Payment is handled by Stripe. We never see or store your card
          details.
        </p>
      </form>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-title"
        className="relative my-auto w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded px-2 text-xl text-slate-400 hover:text-slate-600"
        >
          ×
        </button>
        <h2 id="checkout-title" className="m-0 pr-8 text-xl font-bold text-slate-900">
          {details.name} · £{details.monthlyPrice}/month
        </h2>
        <p className="mb-5 mt-1 text-sm text-slate-500">{details.tagline}</p>
        {error && (
          <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {body}
      </div>
    </div>
  );
};

export default CheckoutDialog;
